// src/utils/billing.js
//
// SUIVI FINANCIER — logique PARTAGÉE entre Subscriptions.js, Registrations.js
// et AttendancePage.js. Fonctions pures uniquement (aucune lecture/écriture
// Firestore ici) : les trois pages calculent donc exactement la même chose.
//
// Une échéance est considérée payée en combinant 4 sources :
//   1. Collection `payments` (Mollie, écrite par les Cloud Functions) :
//      refPath = registrations/{id}, installmentNumber = n° d'échéance.
//   2. `installmentsPaid` (compteur écrit par mollieWebhook).
//   3. `registrations/{id}.manualPayments[]` : encaissements saisis dans
//      l'admin (espèces, virement, terminal…) — écrits par billingActions.js.
//   4. `paymentStatus === 'paid'` (ancien bouton « Marquer payé ») : ne vaut
//      que pour la 1re échéance / le paiement unique.
//
// L'échéancier est RECALCULÉ ici, y compris pour les abonnements payés à la
// main (qui n'ont pas de nextPaymentDate) : même cadence que Mollie (mois
// calendaire, même jour plafonné au 28), jusqu'en juin de la saison.
// Les réglages saisis par l'admin (`billingOverrides`) priment, sans jamais
// toucher aux champs gérés par les Cloud Functions.

export const OVERDUE_GRACE_DAYS = 5;   // un prélèvement SEPA peut rester "pending" quelques jours
export const DUE_SOON_DAYS = 14;

const pad = (n) => String(n).padStart(2, '0');
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ── Dates ────────────────────────────────────────────────────────────────────

export const readDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Date « jour » à minuit LOCAL. Une chaîne 'YYYY-MM-DD' pure est lue en heure
 * locale (new Date('2026-11-05') serait lue en UTC et pourrait tomber la
 * veille) ; une chaîne ISO complète ou un Timestamp passe par readDate.
 */
export const readDay = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, d] = value.trim().split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = readDate(value);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
};

export const dayKey = (value) => {
  const d = readDay(value);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : null;
};

export const todayStart = () => {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
};

export const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

export const formatDay = (value) => {
  const d = readDay(value);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '—';
};

export const money = (value) => {
  const n = round2(value);
  return `${n.toFixed(Number.isInteger(n) ? 0 : 2)} €`;
};

export const isPaidStatus = (status) =>
  ['paid', 'payé', 'paye'].includes(String(status || '').toLowerCase());

// ── Saison (septembre → juin) ────────────────────────────────────────────────

export const seasonOf = (value) => {
  const d = readDay(value);
  if (!d) return null;
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1; // juillet → saison suivante
  return `${y}-${y + 1}`;
};

export const currentSeason = () => seasonOf(new Date());

const seasonEnd = (start) =>
  (start.getMonth() >= 6 ? new Date(start.getFullYear() + 1, 5, 30) : new Date(start.getFullYear(), 5, 30));

// ── Libellés ─────────────────────────────────────────────────────────────────

export const FREQUENCY_LABELS = {
  monthly:   'Mensuel',
  semester:  'Semestriel',
  semester1: 'Semestre 1',
  semester2: 'Semestre 2',
  annual:    'Annuel',
};

export const PAYMENT_MODE_LABELS = { mollie: 'Mollie (auto)', manual: 'Virement / espèces' };

export const MANUAL_METHOD_LABELS = {
  cash:       'Espèces',
  transfer:   'Virement',
  bancontact: 'Bancontact (terminal)',
  other:      'Autre',
};

export const METHOD_LABELS = {
  directdebit: 'Prélèvement SEPA',
  bancontact:  'Bancontact',
  creditcard:  'Carte bancaire',
  ...MANUAL_METHOD_LABELS,
};

export const PAYMENT_DOC_STATUS = {
  paid:     { label: 'Payé',     bg: '#e8f5e9', color: '#2e7d32' },
  open:     { label: 'En cours', bg: '#fff3e0', color: '#e65100' },
  pending:  { label: 'En cours', bg: '#fff3e0', color: '#e65100' },
  failed:   { label: 'Échoué',   bg: '#ffebee', color: '#c62828' },
  expired:  { label: 'Expiré',   bg: '#eceff1', color: '#455a64' },
  canceled: { label: 'Annulé',   bg: '#eceff1', color: '#455a64' },
};

/** Statut global d'une inscription (résumé de son échéancier) */
export const BILLING_STATUS = {
  failed:          { label: 'Paiement échoué',   bg: '#ffebee', color: '#c62828', rank: 7 },
  overdue:         { label: 'En retard',         bg: '#ffebee', color: '#c62828', rank: 6 },
  pending_mandate: { label: 'Mandat en attente', bg: '#fff3e0', color: '#e65100', rank: 5 },
  due:             { label: 'À payer',           bg: '#fff3e0', color: '#e65100', rank: 4 },
  processing:      { label: 'Paiement en cours', bg: '#fff8e1', color: '#f57f17', rank: 3 },
  up_to_date:      { label: 'À jour',            bg: '#e8f5e9', color: '#2e7d32', rank: 2 },
  paid:            { label: 'Soldé',             bg: '#eceff1', color: '#455a64', rank: 1 },
};
export const billingStatusMeta = (status) =>
  BILLING_STATUS[status] || { label: status || '—', bg: '#f5f5f5', color: '#616161', rank: 0 };

/** Pire statut d'une liste (pour agréger plusieurs inscriptions d'un élève) */
export const worstStatus = (statuses) =>
  statuses.reduce((worst, s) => (billingStatusMeta(s).rank > billingStatusMeta(worst).rank ? s : worst), 'paid');

/** Statut d'une échéance */
export const INSTALLMENT_STATUS = {
  paid:       { label: 'Payée',    bg: '#e8f5e9', color: '#2e7d32' },
  failed:     { label: 'Échouée',  bg: '#ffebee', color: '#c62828' },
  overdue:    { label: 'En retard', bg: '#ffebee', color: '#c62828' },
  processing: { label: 'En cours', bg: '#fff8e1', color: '#f57f17' },
  due:        { label: 'À payer',  bg: '#fff3e0', color: '#e65100' },
  soon:       { label: 'Bientôt',  bg: '#e3f2fd', color: '#1565c0' },
  upcoming:   { label: 'À venir',  bg: '#f5f5f5', color: '#616161' },
};

// ── Identité d'un élève (dédoublonnage) ──────────────────────────────────────

export const normalizeText = (s) =>
  String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const looksLikeDocId = (v) => typeof v === 'string' && v.length >= 15;

/**
 * Personnes d'une inscription. Les anciennes inscriptions app n'ont parfois
 * que targetStudentName : on en reconstruit une personne pour ne pas les perdre.
 */
export const registrationPersons = (reg) => {
  if (Array.isArray(reg?.persons) && reg.persons.length > 0) return reg.persons;
  if (reg?.targetStudentName) {
    const parts = String(reg.targetStudentName).trim().split(/\s+/);
    return [{ firstName: parts[0] || '', lastName: parts.slice(1).join(' '), studentId: reg.targetStudentId || undefined }];
  }
  return [];
};

export const personName = (p) => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();

/**
 * Clés d'identité d'une personne. Deux personnes partageant AU MOINS une clé
 * sont considérées comme le même élève.
 *  • sid:…  identifiants Firestore (studentId, targetStudentId…)
 *  • name:… prénom + nom normalisés (accents/majuscules/espaces ignorés) —
 *           c'est ce qui recolle une inscription site (sans studentId) avec
 *           une carte 10 prise dans l'app.
 * appUserId n'est utilisé QUE pour une inscription adulte à une personne :
 * pour un parent, il est commun à tous ses enfants et fusionnerait les frères
 * et sœurs (bug de l'ancienne déduplication).
 */
export const personIdentityKeys = (person, reg = {}) => {
  const keys = [];
  const single = registrationPersons(reg).length <= 1;
  if (person?.studentId) keys.push(`sid:${person.studentId}`);
  if (looksLikeDocId(person?.id) && person.id !== person.studentId) keys.push(`sid:${person.id}`);
  if (single && reg.targetStudentId) keys.push(`sid:${reg.targetStudentId}`);
  if (single && reg.appUserId && reg.registrationType !== 'child') keys.push(`sid:${reg.appUserId}`);
  const first = normalizeText(person?.firstName);
  const last  = normalizeText(person?.lastName);
  if (first && last.length >= 2) keys.push(`name:${first} ${last}`);
  return Array.from(new Set(keys));
};

export const registrationContacts = (reg) => {
  const emails = [reg?.contactInfo?.email, reg?.parentInfo?.parentEmail].filter(Boolean);
  const phones = [reg?.contactInfo?.phone, reg?.parentInfo?.parentPhone].filter(Boolean);
  return { emails, phones };
};

/**
 * Regroupe les inscriptions par élève (ou fratrie) : deux inscriptions qui
 * partagent une personne finissent dans le même groupe. Une inscription
 * famille (2 enfants) relie les deux enfants dans un seul groupe, ce qui
 * évite de compter deux fois le même paiement.
 */
export const groupRegistrationsByStudent = (regs) => {
  const parent = new Map();
  const find = (x) => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    let c = x;
    while (parent.get(c) !== r) { const n = parent.get(c); parent.set(c, r); c = n; }
    return r;
  };
  const union = (a, b) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(rb, ra); };

  regs.forEach((reg) => parent.set(reg.id, reg.id));
  const keyOwner = new Map();
  regs.forEach((reg) => {
    registrationPersons(reg).forEach((p) => {
      personIdentityKeys(p, reg).forEach((k) => {
        if (keyOwner.has(k)) union(keyOwner.get(k), reg.id);
        else keyOwner.set(k, reg.id);
      });
    });
  });

  const buckets = new Map();
  regs.forEach((reg) => {
    const root = find(reg.id);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(reg);
  });

  return Array.from(buckets.entries()).map(([root, list]) => {
    const names = [];
    const seenNames = new Set();
    const emails = new Set();
    const phones = new Set();
    list.forEach((reg) => {
      registrationPersons(reg).forEach((p) => {
        const label = personName(p);
        const k = normalizeText(label);
        if (label && !seenNames.has(k)) { seenNames.add(k); names.push(label); }
      });
      const c = registrationContacts(reg);
      c.emails.forEach((e) => emails.add(e));
      c.phones.forEach((ph) => phones.add(ph));
    });
    const emailList = Array.from(emails);
    if (names.length === 0) names.push(emailList[0] || 'Sans nom');
    return {
      key: root,
      registrations: list,
      names,
      label: names.join(', '),
      emails: emailList,
      phones: Array.from(phones),
    };
  });
};

// ── Type d'inscription ───────────────────────────────────────────────────────

/** 'trial' | 'card' | 'single' | 'subscription' (tout le reste : mensuel, semestre, annuel, Formation Crew…) */
export const registrationKind = (reg) => {
  if (reg?.plan === 'essai')   return 'trial';
  if (reg?.plan === 'carte10') return 'card';
  if (reg?.plan === 'unique')  return 'single';
  return 'subscription';
};

const RECURRING_FREQUENCIES = new Set(['monthly', 'semester']);

export const isRecurring = (reg) =>
  registrationKind(reg) === 'subscription' && (
    Number(reg?.billingOverrides?.installmentsTotal) > 0
    || Number(reg?.installmentsTotal) > 0
    || RECURRING_FREQUENCIES.has(reg?.paymentFrequency)
  );

export const registrationStartDate = (reg) =>
  readDay(reg?.billingOverrides?.startDate)
  || readDay(reg?.subscriptionStartDate)
  || readDay(reg?.subscriptionAnchorDate)
  || readDay(reg?.createdAt);

export const registrationCourseIds = (reg) => {
  const s = new Set();
  if (reg?.courseId) s.add(reg.courseId);
  (reg?.selectedCourseIds || []).forEach((id) => id && s.add(id));
  (reg?.selectedCourses || []).forEach((c) => c?.id && s.add(c.id));
  return Array.from(s);
};

export const registrationCourseLabel = (reg) => {
  if (Array.isArray(reg?.selectedCourses) && reg.selectedCourses.length > 0) {
    return reg.selectedCourses.map((c) => c.title || c.id || '').filter(Boolean).join(', ');
  }
  return reg?.courseTitle || '—';
};

export const planLabel = (reg) => reg?.planName || reg?.plan || 'Formule';

// ── Carte 10 cours ───────────────────────────────────────────────────────────

export const cardCredits = (reg) => {
  const consumed = Array.isArray(reg?.consumedLessons) ? reg.consumedLessons : [];
  const total = Number(reg?.creditsTotal || reg?.includedCourses || reg?.courseCredits || 10);
  const used  = Math.max(Number(reg?.creditsUsed || 0), consumed.length);
  return { total, used, remaining: Math.max(total - used, 0), consumed };
};

/** Une consommation de carte couvre-t-elle cette séance ? (entrée sans courseId = ancienne donnée, on l'accepte) */
export const consumptionMatches = (cl, dateKeyValue, courseId) =>
  cl?.dateKey === dateKeyValue && (!cl.courseId || !courseId || cl.courseId === courseId);

/** Priorité d'une inscription quand un élève en a plusieurs sur la même séance */
export const registrationPriority = (reg) => {
  const kind = registrationKind(reg);
  if (kind === 'subscription') return 40;
  if (kind === 'card') return cardCredits(reg).remaining > 0 ? 30 : 20;
  if (kind === 'single') return 10;
  return 0;
};

// ── Échéancier ───────────────────────────────────────────────────────────────

const addMonthsAnchored = (base, n) =>
  new Date(base.getFullYear(), base.getMonth() + n, Math.min(base.getDate(), 28));

const monthsBetweenInclusive = (a, b) =>
  (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1;

const installmentCount = (reg, start) => {
  const ov = Number(reg?.billingOverrides?.installmentsTotal);
  if (ov > 0) return ov;
  const t = Number(reg?.installmentsTotal);
  if (t > 0) return t;
  const end = seasonEnd(start);
  if (reg?.paymentFrequency === 'semester') {
    return start < new Date(end.getFullYear(), 1, 1) ? 2 : 1;
  }
  // Inscription prise en juillet/août : engagement septembre → juin = 10 mois
  if (start.getMonth() === 6 || start.getMonth() === 7) return 10;
  return Math.max(1, monthsBetweenInclusive(start, end));
};

const buildDueDates = (reg, start, total, mollieCounter) => {
  const semester = reg?.paymentFrequency === 'semester';
  const end = seasonEnd(start);
  const dates = [];
  for (let i = 0; i < total; i += 1) {
    if (i === 0) dates.push(start);
    else if (semester && i === 1 && start < new Date(end.getFullYear(), 1, 1)) dates.push(new Date(end.getFullYear(), 1, 1));
    else dates.push(addMonthsAnchored(start, semester ? 6 * i : i));
  }
  // Mollie connaît la VRAIE date du prochain prélèvement : on s'aligne dessus.
  const next = readDay(reg?.nextPaymentDate);
  if (reg?.paymentMode === 'mollie' && next && mollieCounter > 0 && mollieCounter < total) {
    for (let i = mollieCounter; i < total; i += 1) {
      dates[i] = i === mollieCounter ? next : (semester ? dates[i] : addMonthsAnchored(next, i - mollieCounter));
    }
  }
  return dates;
};

const paymentDocAmount = (p) => Number(p?.amountGross ?? p?.amount?.value ?? p?.amount ?? 0) || 0;

/**
 * Calcule l'échéancier complet d'une inscription et son résumé.
 * @param reg          document registration (avec id)
 * @param paymentDocs  documents `payments` Mollie de cette inscription (optionnel)
 */
export const computeBilling = (reg, paymentDocs = []) => {
  const kind = registrationKind(reg);
  const recurring = isRecurring(reg);
  const today = todayStart();
  const graceLimit = addDays(today, -OVERDUE_GRACE_DAYS);
  const soonLimit  = addDays(today, DUE_SOON_DAYS);

  const start = registrationStartDate(reg) || today;
  const pd = reg?.paymentDetails || {};
  const ov = reg?.billingOverrides || {};
  const declared = round2(pd.totalAmount || pd.amount || 0);
  const instAmt  = round2(ov.installmentAmount || reg?.installmentAmount || 0);
  const firstOv  = round2(ov.firstInstallmentAmount || 0);
  const isMollie = reg?.paymentMode === 'mollie';
  const mollieCounter = Number(reg?.installmentsPaid || 0);

  let dueDates;
  let amounts;
  let amountEstimated = false;
  if (recurring) {
    const total = installmentCount(reg, start);
    dueDates = buildDueDates(reg, start, total, mollieCounter);
    const first  = firstOv || declared || instAmt;
    const others = instAmt || declared;
    amountEstimated = !instAmt && total > 1;
    amounts = dueDates.map((_, i) => (i === 0 ? first : others));
  } else {
    dueDates = [start];
    amounts = [firstOv || declared];
  }

  const docsByN = new Map();
  (paymentDocs || []).forEach((p) => {
    const n = Number(p.installmentNumber) || 1;
    if (!docsByN.has(n)) docsByN.set(n, []);
    docsByN.get(n).push(p);
  });
  const manual = Array.isArray(reg?.manualPayments) ? reg.manualPayments : [];
  // paymentStatus 'paid' ne compte comme « ancien marquage » que s'il n'a pas
  // été posé automatiquement par un encaissement manuel (sinon on compterait
  // un paiement partiel comme complet).
  const legacyPaid = isPaidStatus(reg?.paymentStatus) && !manual.some((m) => m.setPaymentStatus);

  const installments = dueDates.map((dueDate, i) => {
    const n = i + 1;
    const amount = round2(amounts[i] || 0);
    const docs = (docsByN.get(n) || []).slice().sort(
      (a, b) => (readDate(a.createdAt)?.getTime() || 0) - (readDate(b.createdAt)?.getTime() || 0)
    );
    const manualEntries = manual.filter((m) => (Number(m.installmentNumber) || 1) === n);

    let paidAmount = 0;
    let paidAt = null;
    const sources = [];
    const notePaidAt = (d) => { if (d && (!paidAt || d > paidAt)) paidAt = d; };

    manualEntries.forEach((m) => {
      paidAmount += Number(m.amount || 0);
      sources.push('manual');
      notePaidAt(readDay(m.paidAt) || readDate(m.recordedAt));
    });
    docs.filter((p) => p.status === 'paid').forEach((p) => {
      paidAmount += paymentDocAmount(p) || amount;
      sources.push('mollie');
      notePaidAt(readDate(p.paidAt || p.createdAt));
    });
    if (n <= mollieCounter && !sources.includes('mollie')) {
      paidAmount = Math.max(paidAmount, amount);
      sources.push('mollie');
    }
    if (n === 1 && legacyPaid && paidAmount < amount + 0.001 && !sources.includes('mollie')) {
      paidAmount = Math.max(paidAmount, amount);
      sources.push('status');
    }
    paidAmount = round2(paidAmount);

    const settled = sources.length > 0 && (amount <= 0 || paidAmount >= amount - 0.01);
    const remaining = settled ? 0 : round2(Math.max(amount - paidAmount, 0));
    const lastDoc = docs[docs.length - 1];

    let status;
    if (settled) status = 'paid';
    else if (
      (lastDoc && lastDoc.status === 'failed')
      || (isMollie && reg?.subscriptionStatus === 'payment_failed' && n === mollieCounter + 1)
      || (reg?.subscriptionStatus === 'mandate_failed' && n === 1)
    ) status = 'failed';
    else if (
      (lastDoc && ['open', 'pending', 'authorized'].includes(lastDoc.status))
      || (isMollie && reg?.chargeInProgress && n === mollieCounter + 1)
      || (n === 1 && reg?.subscriptionStatus === 'pending_mandate')
    ) status = 'processing';
    else if (dueDate < graceLimit) status = 'overdue';
    else if (dueDate <= today) status = 'due';
    else if (dueDate <= soonLimit) status = 'soon';
    else status = 'upcoming';

    return {
      number: n,
      dueDate,
      amount,
      paidAmount,
      remaining,
      partial: !settled && paidAmount > 0,
      status,
      paidAt,
      sources: Array.from(new Set(sources)),
      manualEntries,
      paymentDocs: docs,
      overdueDays: status === 'overdue' || status === 'failed'
        ? Math.max(0, Math.round((today - dueDate) / 86400000))
        : 0,
    };
  });

  const sum = (arr, f) => round2(arr.reduce((acc, x) => acc + f(x), 0));
  const expected = sum(installments, (x) => x.amount);
  const paid = sum(installments, (x) => (x.amount > 0 ? Math.min(x.paidAmount, x.amount) : x.paidAmount));
  const remaining = sum(installments, (x) => x.remaining);
  const late = installments.filter((x) => x.status === 'overdue' || x.status === 'failed');
  const paidCount = installments.filter((x) => x.status === 'paid').length;

  let status;
  if (installments.some((x) => x.status === 'failed')) status = 'failed';
  else if (late.length > 0) status = 'overdue';
  else if (paidCount === installments.length) status = 'paid';
  else if (reg?.subscriptionStatus === 'pending_mandate') status = 'pending_mandate';
  else if (installments.some((x) => x.status === 'processing')) status = 'processing';
  else if (installments.some((x) => x.status === 'due')) status = 'due';
  else status = 'up_to_date';

  return {
    kind,
    recurring,
    isMollie,
    start,
    end: recurring ? seasonEnd(start) : null,
    installments,
    total: installments.length,
    paidCount,
    expected,
    paid,
    remaining,
    overdueAmount: sum(late, (x) => x.remaining),
    overdueCount: late.length,
    dueToDate: sum(installments.filter((x) => x.dueDate <= today), (x) => x.remaining),
    next: installments.find((x) => x.status !== 'paid') || null,
    status,
    amountEstimated,
    amountUnknown: expected === 0,
  };
};

// ── Badge « paiement » affiché dans la feuille de présences ──────────────────

/**
 * Résumé court de la situation d'un élève, pour la page présences.
 * tone : 'error' | 'warning' | 'default'. null = rien à signaler.
 */
export const attendanceBadge = (reg) => {
  if (!reg) return null;
  const kind = registrationKind(reg);
  if (kind === 'trial') return null;
  const b = computeBilling(reg);
  if (b.status === 'failed') {
    return { label: 'Paiement échoué', tone: 'error', tooltip: `Reste dû : ${money(b.overdueAmount)}` };
  }
  if (b.status === 'overdue') {
    const late = b.installments.filter((x) => x.status === 'overdue');
    return {
      label: `Retard ${money(b.overdueAmount)}`,
      tone: 'error',
      tooltip: late.map((x) => `Échéance ${x.number} du ${formatDay(x.dueDate)} : ${money(x.remaining)}`).join(' · '),
    };
  }
  if (kind === 'card') {
    const c = cardCredits(reg);
    if (c.remaining === 0) return { label: 'Carte épuisée', tone: 'error', tooltip: `${c.used}/${c.total} cours utilisés` };
    if (b.status === 'due') return { label: `Carte non payée · ${c.remaining} rest.`, tone: 'warning', tooltip: '' };
    return { label: `Carte ${c.used}/${c.total}`, tone: c.remaining <= 2 ? 'warning' : 'default', tooltip: `${c.remaining} cours restant(s)` };
  }
  if (b.status === 'pending_mandate') return { label: 'Mandat en attente', tone: 'warning', tooltip: '' };
  if (b.status === 'due') return { label: `À payer ${money(b.dueToDate)}`, tone: 'warning', tooltip: '' };
  return null;
};

export const BADGE_TONES = {
  error:   { bg: '#ffebee', color: '#c62828' },
  warning: { bg: '#fff3e0', color: '#e65100' },
  default: { bg: '#f3e8fd', color: '#7C3AED' },
};
